import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

interface SessionUser {
  id: string;
  parishId: string;
  dioceseId: string;
  displayName: string;
  roles: string[];
}

const STORAGE_KEY = 'kanisani.session.v1';

function loadFromStorage(): { token: string | null; user: SessionUser | null; expiresAt: string | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { token: null, user: null, expiresAt: null };
    const obj = JSON.parse(raw);
    if (obj?.expiresAt && new Date(obj.expiresAt) < new Date()) {
      localStorage.removeItem(STORAGE_KEY);
      return { token: null, user: null, expiresAt: null };
    }
    return { token: obj.token, user: obj.user, expiresAt: obj.expiresAt };
  } catch {
    return { token: null, user: null, expiresAt: null };
  }
}

export const useAuthStore = defineStore('auth', () => {
  const initial = loadFromStorage();
  const token = ref<string | null>(initial.token);
  const user = ref<SessionUser | null>(initial.user);
  const expiresAt = ref<string | null>(initial.expiresAt);

  const isAuthenticated = computed(() => !!token.value);

  function setSession(payload: { token: string; user: SessionUser; expiresAt: string }) {
    token.value = payload.token;
    user.value = payload.user;
    expiresAt.value = payload.expiresAt;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }

  function clear() {
    token.value = null;
    user.value = null;
    expiresAt.value = null;
    localStorage.removeItem(STORAGE_KEY);
  }

  return { token, user, expiresAt, isAuthenticated, setSession, clear };
});
